import React, { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Map as MapIcon,
  FileSpreadsheet,
  History as HistoryIcon,
  Users,
  PencilRuler,
  Settings as SettingsIcon,
  LogOut,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { initials } from "@/lib/status";

const NAV = [
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "admin-nav-dashboard" },
  { to: "/admin/map", label: "Live Map", icon: MapIcon, testid: "admin-nav-map" },
  { to: "/admin/report", label: "Daily Report", icon: FileSpreadsheet, testid: "admin-nav-report" },
  { to: "/admin/history", label: "History", icon: HistoryIcon, testid: "admin-nav-history" },
  { to: "/admin/workers", label: "Workers", icon: Users, testid: "admin-nav-workers" },
  { to: "/admin/map-editor", label: "Map Editor", icon: PencilRuler, testid: "admin-nav-editor" },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon, testid: "admin-nav-settings" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const [info, setInfo] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/app-info").then((r) => setInfo(r.data)).catch(() => {});
  }, []);

  const outletCtx = React.useMemo(() => ({ info }), [info]);

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-semibold transition ${
      isActive ? "bg-primary text-white shadow-sm" : "text-stone-600 hover:bg-stone-100"
    }`;

  return (
    <div className="min-h-screen bg-background lg:flex">
      {/* Sidebar */}
      <aside className="sticky top-0 z-40 hidden h-screen w-64 shrink-0 flex-col border-r border-stone-200 bg-white lg:flex">
        <div className="flex items-center gap-2.5 px-5 py-5">
          <img src="/plaza-mark.png" alt="Plaza" className="h-11 w-11 object-contain" />
          <div className="leading-tight">
            <p className="font-heading text-base font-extrabold tracking-tight" data-testid="admin-beach-name">
              {info?.beach_name || "Beach"}
            </p>
            <p className="text-xs text-stone-500">Owner Console</p>
          </div>
        </div>
        <nav className="flex-1 space-y-1 px-3 py-2">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} className={linkClass} data-testid={n.testid}>
              <n.icon className="h-5 w-5" strokeWidth={2} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-stone-200 p-3">
          <div className="flex items-center gap-3 rounded-xl px-2 py-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-stone-900 text-sm font-bold text-white">
              {initials(user?.name)}
            </div>
            <div className="min-w-0 flex-1 leading-tight">
              <p className="truncate text-sm font-bold">{user?.name}</p>
              <p className="text-xs text-stone-500">Administrator</p>
            </div>
            <button
              data-testid="admin-logout-btn"
              onClick={async () => {
                await logout();
                navigate("/login");
              }}
              className="flex h-9 w-9 items-center justify-center rounded-lg text-stone-500 transition hover:bg-stone-100"
            >
              <LogOut className="h-5 w-5" strokeWidth={2.2} />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile top nav */}
      <div className="sticky top-0 z-40 border-b border-stone-200 bg-white/90 backdrop-blur-xl lg:hidden">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <img src="/plaza-mark.png" alt="Plaza" className="h-10 w-10 object-contain" />
            <p className="font-heading text-sm font-extrabold">{info?.beach_name || "Beach"}</p>
          </div>
          <button
            data-testid="admin-logout-btn-mobile"
            onClick={async () => {
              await logout();
              navigate("/login");
            }}
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-stone-200 text-stone-600"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
        <div className="bb-scroll flex gap-1.5 overflow-x-auto px-3 pb-2">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`${n.testid}-mobile`}
              className={({ isActive }) =>
                `whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-bold transition ${
                  isActive ? "bg-primary text-white" : "bg-stone-100 text-stone-600"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </div>
      </div>

      <main className="min-w-0 flex-1">
        <div className="mx-auto w-full max-w-7xl px-4 py-6 lg:px-8 lg:py-8">
          <Outlet context={outletCtx} />
        </div>
      </main>
    </div>
  );
}
